var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { assert, datetime } from '@codegrade/utils';
import { SiteSettingsStore } from '@/store';
import { CacheDecorator } from '@/utils/cache';
/** The class that represents a submission by a user or group of users. */
export class Submission extends api.Work {
    /** The cache used for expensive getters. */
    #cache = new CacheDecorator();
    /** The public cache of the submission. */
    get _cache() {
        return this.#cache;
    }
    /** @param props The data to construct the submission with. */
    constructor(props) {
        super(props);
        Object.freeze(this);
    }
    /**
     * Get how much time we should wait until a submission should have an ATv2 result.
     *
     * @param course The course to get the tenant of.
     * @returns The time we should stop trying to load the ATv2 results because they will not appear
     * anymore.
     */
    getRetryATv2ResultUntil(course) {
        const extraTime = SiteSettingsStore.getSetting()('NEW_AUTO_TEST_OLD_SUBMISSION_AGE', course.tenantId);
        return this.createdAt.clone().add(extraTime);
    }
    /** @returns The moment the submission was created (with year). */
    get formattedCreatedAt() {
        return datetime.readableFormatDate(this.createdAt);
    }
    /**
     * The moment the submission was created, in the shortest format.
     *
     * @returns The created at of this assignment, in a format that is easily readable for the
     * user.
     */
    get readableCreatedAt() {
        return datetime.formatMoment(this.createdAt, 'MMM DD, HH:mm');
    }
    /**
     * Check if the `createAt` of this submission is after a certain time.
     *
     * @param time The time to check, if `Nothing` the `createdAt` is never after the time.
     * @returns If the submission was created after the given time.
     */
    _isAfter(time) {
        return time.caseOf({
            Just: t => this.createdAt.isAfter(t),
            Nothing: () => false,
        });
    }
    /** @returns Whether the submission is late. */
    get isLate() {
        const res = this._isAfter(this.timeframe.deadline) || this._isAfter(this.timeframe.lockDate);
        // If we are late a late reason should be known.
        if (!IS_PRODUCTION) {
            assert.AssertionError.assert(this.lateReason.isJust() === res);
        }
        return res;
    }
    /** @returns Whether the submission is late. */
    get lateReason() {
        const checkLate = this.createdAt.isAfter.bind(this.createdAt);
        const maybeDeadline = this.timeframe.deadline.filter(checkLate);
        const maybeLockdate = this.timeframe.lockDate.filter(checkLate);
        return maybeDeadline.caseOf({
            Just: deadline => maybeLockdate.caseOf({
                Just: (lockDate) => Maybe.of(Object.freeze({
                    tag: 'both',
                    deadline,
                    lockDate,
                })),
                Nothing: () => Maybe.of(Object.freeze({ tag: 'deadline', deadline })),
            }),
            Nothing: () => maybeLockdate.map(lockDate => Object.freeze({
                tag: 'lock-date',
                lockDate,
            })),
        });
    }
}
__decorate([
    CacheDecorator.cache
], Submission.prototype, "formattedCreatedAt", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "readableCreatedAt", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "isLate", null);
__decorate([
    CacheDecorator.cache
], Submission.prototype, "lateReason", null);
